/**
 * Which path `initPostHog()` took, as a value.
 *
 * Every early return in `initPostHog()` produces the same observable result:
 * total browser silence for that page load, because `capture()` is gated on the
 * `initialized` flag those returns skip. A guard that fired and a page that
 * never loaded are therefore indistinguishable in PostHog, which is exactly the
 * hole the 2026-09-13 read could not close: 14 of 132 accounts that finished the
 * onboarding wizard between 2026-06-15 and 2026-09-13 emit no `$lib='web'` event
 * on app.overlayqa.com, ever, while the same 14 emit 732 extension, 304
 * server-side and 46 api.overlayqa.com web events.
 *
 * Pure and separate from analytics.ts on purpose: analytics.ts reads
 * `import.meta.env`, which this jest setup cannot evaluate, so a behavioural
 * test can only reach the decision if the decision lives here. Same split
 * posthogConfig.ts and scannerNoise.ts already use.
 */
export type InitBranch = 'no_key' | 'already_initialized' | 'localhost' | 'scanner_ua' | 'init';

export interface InitBranchInput {
  hasKey: boolean;
  alreadyInitialized: boolean;
  hostname: string;
  allowLocalhost: boolean;
  isScannerUa: boolean;
}

/**
 * Model the early returns in `initPostHog()`, IN SOURCE ORDER.
 *
 * The order is load-bearing rather than cosmetic: a model that disagreed with
 * the code would name a branch the code did not take, and that is worse than
 * naming nothing, because it would point the next investigation somewhere false.
 */
export function resolveInitBranch(input: InitBranchInput): InitBranch {
  // `if (initialized || !POSTHOG_KEY) return;` — one statement, two reasons, so
  // they are split here to keep the report actionable. The combination is
  // unreachable in practice: without a key `initialized` is never set.
  if (input.alreadyInitialized) return 'already_initialized';
  if (!input.hasKey) return 'no_key';

  // The localhost refusal, and the build-time seam that reopens it for a harness
  // build (VITE_ALLOW_LOCALHOST_ANALYTICS). When the seam is open the guard does
  // not fire at all, so the branch is `init`, not `localhost`.
  if (!input.allowLocalhost && (input.hostname === 'localhost' || input.hostname === '127.0.0.1')) {
    return 'localhost';
  }

  if (input.isScannerUa) return 'scanner_ua';

  return 'init';
}

/**
 * Whether this branch may send a beacon.
 *
 * Three branches are deliberately silent, and each silence means something
 * different — read them as three distinct facts, never as one absence:
 *
 *   no_key              structurally unreportable. A capture needs an api_key to
 *                       authenticate with and there is none, so this branch can
 *                       never be counted from the browser at all.
 *   localhost           refused on purpose. The guard exists so nothing a
 *                       developer does on their own machine lands in project
 *                       375980, and a beacon reporting the refusal would put it
 *                       back in. A harness build opens the seam instead, and
 *                       then the branch is `init` and reports normally.
 *   already_initialized suppressed so one page load can never double-count.
 */
export function isReportableBranch(branch: InitBranch): boolean {
  return branch === 'init' || branch === 'scanner_ua';
}

/**
 * A hand-written route literal: lowercase, short, no separators beyond a dash.
 * A token, uuid, slug or record id fails it, which is the point.
 */
const ROUTE_LITERAL = /^[a-z][a-z0-9-]{0,31}$/;

/**
 * The route SHAPE for a beacon, never the route VALUE.
 *
 * These beacons ride raw `fetch` so they survive the pipeline under
 * investigation, and that same property means they BYPASS `scrubUrlProperties`
 * in posthogConfig.ts -- the fail-closed filter that stops a credential in
 * window.location reaching a third-party analytics vendor. That filter strips
 * QUERY strings, because that is where the dashboard's known secrets live.
 *
 * Two routes put a bearer token in the PATH, where no query scrub reaches it:
 * `/invite/:token` and `/review-handoff/:token` (App.tsx:170, :194). Since
 * initPostHog() runs at module scope on EVERY page load, a raw pathname would
 * transmit those on the one channel deliberately exempt from the scrub.
 *
 * So only the first segment is emitted, and only when it looks like a route
 * literal. Fail-CLOSED like its sibling: a future `/:token` route reports
 * `other` on the day it is added, rather than leaking until someone remembers
 * to extend a denylist.
 */
export function safePathLabel(pathname: string): string {
  const first = pathname.split('/').filter(Boolean)[0];
  if (!first) return 'root';
  return ROUTE_LITERAL.test(first) ? first : 'other';
}
